import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import SuperviseurLayout from "../../components/layouts/SuperviseurLayout";
import api from "../../services/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const HistoriqueSuperviseur = () => {
  const { utilisateur } = useAuth();
  const [historique, setHistorique] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [commercialSelectionne, setCommercialSelectionne] = useState(null);
  const [historiqueCommercial, setHistoriqueCommercial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCom, setLoadingCom] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [histRes, comRes] = await Promise.all([
          api.get(`/superviseurs/${utilisateur.id}/historique`),
          api.get(`/superviseurs/${utilisateur.id}/commerciaux`),
        ]);
        setHistorique(histRes.data);
        setCommerciaux(comRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (utilisateur?.id) fetchData();
  }, [utilisateur]);

  const fetchHistoriqueCommercial = async (comId) => {
    setLoadingCom(true);
    try {
      const res = await api.get(`/superviseurs/${utilisateur.id}/commerciaux/${comId}/historique`);
      setHistoriqueCommercial(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCom(false);
    }
  };

  const handleSelectCommercial = (com) => {
    if (commercialSelectionne?.id === com.id) {
      setCommercialSelectionne(null);
      setHistoriqueCommercial([]);
    } else {
      setCommercialSelectionne(com);
      fetchHistoriqueCommercial(com.id);
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

  // Données graphe superviseur
  const dataGraphe = [...historique].reverse().map(h => ({
    date: new Date(h.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }),
    coupons: Number(h.total_coupons),
    sorties: Number(h.sorties_credits),
    entrees: Number(h.entrees_credits),
  }));

  if (loading) return (
    <SuperviseurLayout>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
        <div style={{ width: "36px", height: "36px", border: "3px solid #E1F5EE", borderTop: "3px solid #0f6e56", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      </div>
    </SuperviseurLayout>
  );

  return (
    <SuperviseurLayout>
      <div style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ fontSize: "18px", fontWeight: "500", color: "#111827", margin: 0 }}>Historique journalier</h1>
        <p style={{ fontSize: "13px", color: "#6b7280", margin: "4px 0 0" }}>{historique.length} jour(s) enregistré(s)</p>
      </div>

      {historique.length === 0 ? (
        <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", padding: "3rem", textAlign: "center" }}>
          <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📋</div>
          <p style={{ color: "#6b7280", fontSize: "14px" }}>Aucun historique disponible</p>
        </div>
      ) : (
        <>
          {/* Graphe superviseur */}
          <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", padding: "1.25rem", marginBottom: "1.25rem" }}>
            <div style={{ fontSize: "14px", fontWeight: "500", color: "#111827", marginBottom: "1.25rem" }}>Évolution de la caisse</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={dataGraphe}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} />
                <Tooltip contentStyle={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "8px", fontSize: "12px" }} />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <Line type="monotone" dataKey="sorties" name="Sorties (F)" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="entrees" name="Entrées (F)" stroke="#0f6e56" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="coupons" name="Coupons" stroke="#185FA5" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Tableau historique superviseur */}
          <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", overflow: "hidden", marginBottom: "1.25rem" }}>
            <div style={{ padding: "1rem 1.25rem", borderBottom: "0.5px solid #e5e7eb" }}>
              <div style={{ fontSize: "14px", fontWeight: "500", color: "#111827" }}>Historique superviseur</div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f9fafb" }}>
                    {["Date", "Solde début", "Sorties", "Entrées", "Caisse fin", "Coupons", "Pénalités", "Transferts", "Remb. à temps", "Retard <24h", "Retard >24h", "Tardifs"].map(h => (
                      <th key={h} style={{ padding: "0.75rem 1rem", textAlign: "left", fontSize: "11px", color: "#6b7280", fontWeight: "500", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historique.map((row, i) => (
                    <tr key={i} style={{ borderTop: "0.5px solid #f3f4f6" }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f9fafb"}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}
                    >
                      <td style={{ padding: "0.75rem 1rem", fontWeight: "500", color: "#111827", whiteSpace: "nowrap" }}>{formatDate(row.date)}</td>
                      <td style={{ padding: "0.75rem 1rem", whiteSpace: "nowrap" }}>{Number(row.solde_debut_journee).toLocaleString("fr-FR")} F</td>
                      <td style={{ padding: "0.75rem 1rem", color: "#dc2626", whiteSpace: "nowrap" }}>{Number(row.sorties_credits).toLocaleString("fr-FR")} F</td>
                      <td style={{ padding: "0.75rem 1rem", color: "#0f6e56", whiteSpace: "nowrap" }}>{Number(row.entrees_credits).toLocaleString("fr-FR")} F</td>
                      <td style={{ padding: "0.75rem 1rem", fontWeight: "500", whiteSpace: "nowrap" }}>{Number(row.caisse_fin).toLocaleString("fr-FR")} F</td>
                      <td style={{ padding: "0.75rem 1rem" }}>
                        <span style={{ backgroundColor: "#E6F1FB", color: "#185FA5", padding: "2px 8px", borderRadius: "6px", fontWeight: "500" }}>{row.total_coupons}</span>
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: Number(row.total_penalites) > 0 ? "#dc2626" : "#9ca3af", whiteSpace: "nowrap" }}>
                        {Number(row.total_penalites) > 0 ? `${Number(row.total_penalites).toLocaleString("fr-FR")} F` : "—"}
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: "#185FA5", whiteSpace: "nowrap" }}>
                        {Number(row.total_transferts) > 0 ? `${Number(row.total_transferts).toLocaleString("fr-FR")} F` : "—"}
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: "#16a34a", whiteSpace: "nowrap" }}>
                        {Number(row.montant_remb_a_temps) > 0 ? `${Number(row.montant_remb_a_temps).toLocaleString("fr-FR")} F` : "—"}
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: "#ca8a04", whiteSpace: "nowrap" }}>
                        {Number(row.montant_remb_retard_24h) > 0 ? `${Number(row.montant_remb_retard_24h).toLocaleString("fr-FR")} F` : "—"}
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: "#ea580c", whiteSpace: "nowrap" }}>
                        {Number(row.montant_remb_retard_sup_24h) > 0 ? `${Number(row.montant_remb_retard_sup_24h).toLocaleString("fr-FR")} F` : "—"}
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: "#dc2626", whiteSpace: "nowrap" }}>
                        {Number(row.montant_remb_tardifs) > 0 ? `${Number(row.montant_remb_tardifs).toLocaleString("fr-FR")} F` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Historique par commercial */}
          <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", overflow: "hidden" }}>
            <div style={{ padding: "1rem 1.25rem", borderBottom: "0.5px solid #e5e7eb" }}>
              <div style={{ fontSize: "14px", fontWeight: "500", color: "#111827", marginBottom: "0.75rem" }}>Historique par commercial</div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {commerciaux.map(com => (
                  <button key={com.id} onClick={() => handleSelectCommercial(com)} style={{
                    padding: "0.4rem 0.9rem", borderRadius: "9999px", fontSize: "12px",
                    border: commercialSelectionne?.id === com.id ? "none" : "1px solid #e5e7eb",
                    backgroundColor: commercialSelectionne?.id === com.id ? "#0f6e56" : "white",
                    color: commercialSelectionne?.id === com.id ? "white" : "#6b7280",
                    cursor: "pointer", fontWeight: commercialSelectionne?.id === com.id ? "500" : "400"
                  }}>
                    {com.prenom} {com.nom}
                  </button>
                ))}
              </div>
            </div>

            {commercialSelectionne ? (
              loadingCom ? (
                <div style={{ padding: "2rem", textAlign: "center", color: "#6b7280" }}>Chargement...</div>
              ) : historiqueCommercial.length === 0 ? (
                <div style={{ padding: "2rem", textAlign: "center", color: "#6b7280", fontSize: "14px" }}>
                  Aucun historique pour {commercialSelectionne.prenom} {commercialSelectionne.nom}
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f9fafb" }}>
                        {["Date", "Caisse début", "Clients", "Coupons", "Mobilisé", "Encaissé", "Solde rendu", "Pénalités", "Reçu", "Envoyé", "À temps", "Retard <24h", "Retard >24h", "Tardifs"].map(h => (
                          <th key={h} style={{ padding: "0.75rem 1rem", textAlign: "left", fontSize: "11px", color: "#6b7280", fontWeight: "500", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {historiqueCommercial.map((row, i) => (
                        <tr key={i} style={{ borderTop: "0.5px solid #f3f4f6" }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f9fafb"}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}
                        >
                          <td style={{ padding: "0.75rem 1rem", fontWeight: "500", color: "#111827", whiteSpace: "nowrap" }}>{formatDate(row.date)}</td>
                          <td style={{ padding: "0.75rem 1rem", whiteSpace: "nowrap" }}>{Number(row.caisse_debut).toLocaleString("fr-FR")} F</td>
                          <td style={{ padding: "0.75rem 1rem" }}>{row.nb_clients}</td>
                          <td style={{ padding: "0.75rem 1rem" }}>
                            <span style={{ backgroundColor: "#E1F5EE", color: "#0f6e56", padding: "2px 8px", borderRadius: "6px", fontWeight: "500" }}>{row.nb_coupons_vendus}</span>
                          </td>
                          <td style={{ padding: "0.75rem 1rem", color: "#ea580c", whiteSpace: "nowrap" }}>{Number(row.montant_mobilise).toLocaleString("fr-FR")} F</td>
                          <td style={{ padding: "0.75rem 1rem", whiteSpace: "nowrap" }}>{Number(row.montant_encaisse).toLocaleString("fr-FR")} F</td>
                          <td style={{ padding: "0.75rem 1rem", color: "#0f6e56", whiteSpace: "nowrap" }}>{Number(row.solde_rendu).toLocaleString("fr-FR")} F</td>
                          <td style={{ padding: "0.75rem 1rem", whiteSpace: "nowrap", color: Number(row.penalites_recuperees) > 0 ? "#dc2626" : "#9ca3af" }}>
                            {Number(row.penalites_recuperees) > 0 ? `${Number(row.penalites_recuperees).toLocaleString("fr-FR")} F` : "—"}
                          </td>
                          <td style={{ padding: "0.75rem 1rem", color: "#16a34a", whiteSpace: "nowrap" }}>{Number(row.transfert_recu) > 0 ? `+${Number(row.transfert_recu).toLocaleString("fr-FR")} F` : "—"}</td>
                          <td style={{ padding: "0.75rem 1rem", color: "#ea580c", whiteSpace: "nowrap" }}>{Number(row.transfert_envoye) > 0 ? `-${Number(row.transfert_envoye).toLocaleString("fr-FR")} F` : "—"}</td>
                          <td style={{ padding: "0.75rem 1rem", color: "#16a34a", whiteSpace: "nowrap" }}>{Number(row.montant_remb_a_temps) > 0 ? `${Number(row.montant_remb_a_temps).toLocaleString("fr-FR")} F` : "—"}</td>
                          <td style={{ padding: "0.75rem 1rem", color: "#ca8a04", whiteSpace: "nowrap" }}>{Number(row.montant_remb_retard_24h) > 0 ? `${Number(row.montant_remb_retard_24h).toLocaleString("fr-FR")} F` : "—"}</td>
                          <td style={{ padding: "0.75rem 1rem", color: "#ea580c", whiteSpace: "nowrap" }}>{Number(row.montant_remb_retard_sup_24h) > 0 ? `${Number(row.montant_remb_retard_sup_24h).toLocaleString("fr-FR")} F` : "—"}</td>
                          <td style={{ padding: "0.75rem 1rem", color: "#dc2626", whiteSpace: "nowrap" }}>{Number(row.montant_remb_tardifs) > 0 ? `${Number(row.montant_remb_tardifs).toLocaleString("fr-FR")} F` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <div style={{ padding: "2rem", textAlign: "center", color: "#9ca3af", fontSize: "13px" }}>
                Sélectionnez un commercial pour voir son historique
              </div>
            )}
          </div>
        </>
      )}
    </SuperviseurLayout>
  );
};

export default HistoriqueSuperviseur;